import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { batchQueueApi, type BatchQueueItem } from "@/lib/batch-queue";
import { formatDateTime } from "@/lib/format";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "@/hooks/use-toast";

export function BatchQueuePage() {
  const q = useQuery({ queryKey: ["batch-queue"], queryFn: batchQueueApi.list });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canWrite = has("batchqueue:write");

  const runNowMut = useMutation({
    mutationFn: (id: string) => batchQueueApi.runNow(id),
    onSuccess: () => {
      toast.success("Triggered");
      void queryClient.invalidateQueries({ queryKey: ["batch-queue"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => batchQueueApi.cancel(id),
    onSuccess: () => {
      toast.success("Cancelled");
      void queryClient.invalidateQueries({ queryKey: ["batch-queue"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const storageUnavailable = q.data != null && !q.data.storageAvailable;
  const stats = q.data?.stats;

  const columns: DataGridColumn<BatchQueueItem>[] = [
    { id: "id", header: "Job", accessor: (r) => r.id },
    { id: "cron", header: "Schedule", accessor: (r) => r.cron ?? "—" },
    {
      id: "lastRun",
      header: "Last run",
      accessor: (r) => (r.lastExecutionUtc ? formatDateTime(r.lastExecutionUtc) : "—"),
    },
    { id: "lastResult", header: "Last result", accessor: (r) => r.lastJobState ?? "—" },
    {
      id: "nextRun",
      header: "Next run",
      accessor: (r) => (r.nextExecutionUtc ? formatDateTime(r.nextExecutionUtc) : "—"),
    },
    ...(canWrite
      ? [
          {
            id: "actions",
            header: "Actions",
            align: "right" as const,
            accessor: (r: BatchQueueItem) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={runNowMut.isPending}
                  onClick={() => runNowMut.mutate(r.id)}
                >
                  Run now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cancelMut.isPending}
                  onClick={() => cancelMut.mutate(r.id)}
                >
                  Cancel
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <ListPage title="Batch Job Queue" backTo="/admin">
      {storageUnavailable ? (
        <Card>
          <CardContent className="py-4 text-muted-foreground">
            Background job storage is not available in this environment.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {stats && (
            <div className="flex flex-wrap gap-6 text-sm">
              <Stat label="Succeeded" value={stats.succeeded} />
              <Stat label="Failed" value={stats.failed} />
              <Stat label="Processing" value={stats.processing} />
              <Stat label="Scheduled" value={stats.scheduled} />
              <Stat label="Recurring" value={stats.recurring} />
            </div>
          )}
          <DataGrid
            rows={q.data?.recurring ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            totalRows={q.data?.recurring.length ?? 0}
            pageIndex={0}
            pageSize={100}
            onPageChange={() => {}}
            loading={q.isLoading}
            emptyHeadline="No recurring jobs"
          />
        </div>
      )}
    </ListPage>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-lg font-semibold tabular-nums">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}
